// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  );
}

// =============================================================================
// SECTION ID HELPERS
// =============================================================================

class SectionId {
  static #separator = "__";

  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

// =============================================================================
// HTML UPDATE UTILITY
// =============================================================================

class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks.forEach((cb) => cb(newContent));

    const wrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(wrapper, newContent.outerHTML);
    const newNode = wrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((el) => {
      if (el.id) el.id = `${el.id}-${uniqueKey}`;
      if (el.form) el.setAttribute("form", `${el.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks.forEach((cb) => cb(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScript) => {
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach(({ name, value }) =>
        newScript.setAttribute(name, value)
      );
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }
}

// =============================================================================
// DETAILS / SUMMARY ACCESSIBILITY
// =============================================================================

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  const next = summary.nextElementSibling;
  if (next?.id) summary.setAttribute("aria-controls", next.id);

  summary.addEventListener("click", ({ currentTarget }) => {
    currentTarget.setAttribute(
      "aria-expanded",
      !currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (!summary.closest("header-drawer, menu-drawer")) {
    summary.parentElement.addEventListener("keyup", onKeyUpEscape);
  }
});

// =============================================================================
// FOCUS TRAP
// =============================================================================

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = ({ target }) => {
    if (target !== container && target !== last && target !== first) return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    } else if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus?.focus();

  if (
    elementToFocus?.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  elementToFocus?.focus();
}

// =============================================================================
// FOCUS VISIBLE POLYFILL
// =============================================================================

try {
  document.querySelector(":focus-visible");
} catch {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const NAV_KEYS = new Set([
    "ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT",
    "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN",
  ]);

  let currentFocusedElement = null;
  let mouseClick = false;

  window.addEventListener("keydown", ({ code }) => {
    if (NAV_KEYS.has(code.toUpperCase())) mouseClick = false;
  });

  window.addEventListener("mousedown", () => { mouseClick = true; });

  window.addEventListener("focus", () => {
    currentFocusedElement?.classList.remove("focused");
    if (mouseClick) return;
    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add("focused");
  }, true);
}

// =============================================================================
// MEDIA HELPERS
// =============================================================================

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((v) =>
    v.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*")
  );
  document.querySelectorAll(".js-vimeo").forEach((v) =>
    v.contentWindow.postMessage('{"method":"pause"}', "*")
  );
  document.querySelectorAll("video").forEach((v) => v.pause());
  document.querySelectorAll("product-model").forEach((m) => {
    if (m.modelViewerUI) m.modelViewerUI.pause();
  });
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;
  const openDetails = event.target.closest("details[open]");
  if (!openDetails) return;
  const summary = openDetails.querySelector("summary");
  openDetails.removeAttribute("open");
  summary.setAttribute("aria-expanded", false);
  summary.focus();
}

// =============================================================================
// QUANTITY INPUT
// =============================================================================

class QuantityInput extends HTMLElement {
  #quantityUpdateUnsubscriber;

  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.#onInputChange.bind(this));
    this.querySelectorAll("button").forEach((btn) =>
      btn.addEventListener("click", this.#onButtonClick.bind(this))
    );
  }

  connectedCallback() {
    this.#validateQtyRules();
    this.#quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.#validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    this.#quantityUpdateUnsubscriber?.();
  }

  #onInputChange() {
    this.#validateQtyRules();
  }

  #onButtonClick(event) {
    event.preventDefault();
    const { input } = this;
    const previousValue = input.value;
    const isPlus = event.target.name === "plus";

    if (isPlus) {
      if (parseInt(input.dataset.min) > parseInt(input.step) && input.value == 0) {
        input.value = input.dataset.min;
      } else {
        input.stepUp();
      }
    } else {
      if (input.value == 1) {
        const cartItems = this.closest("cart-items") || this.closest("cart-drawer-items");
        cartItems?.updateQuantity(this.closest(".cart-item")?.dataset.index, 0);
      } else {
        input.stepDown();
      }
    }

    if (previousValue !== input.value) input.dispatchEvent(this.changeEvent);

    if (input.dataset.min === previousValue && !isPlus) {
      input.value = parseInt(input.min);
    }
  }

  #validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      this.querySelector(".quantity__button[name='minus']")?.classList.toggle(
        "disabled",
        value <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      this.querySelector(".quantity__button[name='plus']")?.classList.toggle(
        "disabled",
        value >= parseInt(this.input.max)
      );
    }
  }
}

customElements.define("quantity-input", QuantityInput);

// =============================================================================
// UTILITY: DEBOUNCE / THROTTLE / FETCH CONFIG
// =============================================================================

function debounce(fn, wait) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn.apply(this, args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

// =============================================================================
// SHOPIFY COMMON JS
// =============================================================================

if (!window.Shopify) window.Shopify = {};

Shopify.bind = (fn, scope) => fn.bind(scope);

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0; i < selector.options.length; i++) {
    const option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = (target, eventName, callback) =>
  target.addEventListener(eventName, callback, false);

Shopify.postLink = function (path, options = {}) {
  const method = options.method || "post";
  const params = options.parameters || {};
  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  Object.entries(params).forEach(([key, val]) => {
    const field = document.createElement("input");
    field.setAttribute("type", "hidden");
    field.setAttribute("name", key);
    field.setAttribute("value", val);
    form.appendChild(field);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options.hideElement || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry() {
    Shopify.setSelectorByValue(this.countryEl, this.countryEl.getAttribute("data-default"));
    this.countryHandler();
  },

  initProvince() {
    const value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler() {
    const opt = this.countryEl.options[this.countryEl.selectedIndex];
    const provinces = JSON.parse(opt.getAttribute("data-provinces"));
    this.clearOptions(this.provinceEl);

    if (!provinces || provinces.length === 0) {
      this.provinceContainer.style.display = "none";
    } else {
      provinces.forEach(([value, label]) => {
        const option = document.createElement("option");
        option.value = value;
        option.innerHTML = label;
        this.provinceEl.appendChild(option);
      });
      this.provinceContainer.style.display = "";
    }
  },

  clearOptions(selector) {
    while (selector.firstChild) selector.removeChild(selector.firstChild);
  },

  setOptions(selector, values) {
    values.forEach((val) => {
      const opt = document.createElement("option");
      opt.value = val;
      opt.innerHTML = val;
      selector.appendChild(opt);
    });
  },
};

// =============================================================================
// MENU DRAWER
// =============================================================================

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((s) =>
      s.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((btn) => btn.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    const openDetails = event.target.closest("details[open]");
    if (!openDetails) return;
    openDetails === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetails);
  }

  onSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentNode;
    const parentMenu = details.closest(".has-submenu");
    const isOpen = details.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const addTrapFocus = () => {
      trapFocus(summary.nextElementSibling, details.querySelector("button"));
      summary.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    };

    if (details === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summary) : this.openMenuDrawer(summary);
      if (window.matchMedia("(max-width: 990px)").matches) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        details.classList.add("menu-opening");
        summary.setAttribute("aria-expanded", true);
        parentMenu?.classList.add("submenu-open");
        if (!reducedMotion || reducedMotion.matches) {
          addTrapFocus();
        } else {
          summary.nextElementSibling.addEventListener("transitionend", addTrapFocus);
        }
      }, 100);
    }
  }

  openMenuDrawer(summary) {
    setTimeout(() => this.mainDetailsToggle.classList.add("menu-opening"));
    summary.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summary);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (!event) return;
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((d) => d.classList.remove("menu-opening"));
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((m) => m.classList.remove("submenu-open"));
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) {
        this.closeMenuDrawer();
      }
    }, 1000);
  }

  onCloseButtonClick(event) {
    this.closeSubmenu(event.currentTarget.closest("details"));
  }

  closeSubmenu(details) {
    const parentMenu = details.closest(".submenu-open");
    parentMenu?.classList.remove("submenu-open");
    details.classList.remove("menu-opening");
    details.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(details.querySelector("summary"));
    this.closeAnimation(details);
  }

  closeAnimation(details) {
    let animationStart;
    const handleAnimation = (time) => {
      if (!animationStart) animationStart = time;
      if (time - animationStart < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        details.removeAttribute("open");
        const openParent = details.closest("details[open]");
        if (openParent) trapFocus(openParent, details.querySelector("summary"));
      }
    };
    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

// =============================================================================
// HEADER DRAWER
// =============================================================================

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summary) {
    this.header ||= document.querySelector(".section-header");
    this.borderOffset ??= this.closest(".header-wrapper")?.classList.contains("header-wrapper--border-bottom") ? 1 : 0;

    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add("menu-open");
    setTimeout(() => this.mainDetailsToggle.classList.add("menu-opening"));
    summary.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summary);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    if (this.header) {
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    }
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

// =============================================================================
// MODAL DIALOG
// =============================================================================

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", ({ code }) => { if (code.toUpperCase() === "ESCAPE") this.hide(); });

    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", ({ pointerType, target }) => {
        if (pointerType === "mouse" && !target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", ({ target }) => { if (target === this) this.hide(); });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace("shopify-section-", "");
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    popup?.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

// =============================================================================
// BULK MODAL
// =============================================================================

class BulkModal extends HTMLElement {
  connectedCallback() {
    const anchor = document.querySelector(
      `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
    );
    if (!anchor) return;

    new IntersectionObserver((entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() !== "") return;

      const productUrl = this.dataset.url.split("?")[0];
      fetch(`${productUrl}?section_id=bulk-quick-order-list`)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const source = doc.querySelector(".quick-order-list-container")?.parentNode;
          if (source) this.innerHTML = source.innerHTML;
        })
        .catch(console.error);
    }).observe(anchor);
  }
}
customElements.define("bulk-modal", BulkModal);

// =============================================================================
// MODAL OPENER
// =============================================================================

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    this.querySelector("button")?.addEventListener("click", () => {
      document.querySelector(this.getAttribute("data-modal"))?.show(this.querySelector("button"));
    });
  }
}
customElements.define("modal-opener", ModalOpener);

// =============================================================================
// DEFERRED MEDIA
// =============================================================================

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="Deferred-Poster-"]')?.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (this.getAttribute("loaded")) return;

    const content = document.createElement("div");
    content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));
    this.setAttribute("loaded", true);

    const media = this.appendChild(content.querySelector("video, model-viewer, iframe"));
    if (focus) media.focus();
    if (media.nodeName === "VIDEO" && media.getAttribute("autoplay")) media.play();

    const formerStyle = media.getAttribute("style");
    media.setAttribute("style", "display: block;");
    setTimeout(() => media.setAttribute("style", formerStyle), 0);
  }
}
customElements.define("deferred-media", DeferredMedia);

// =============================================================================
// SLIDER COMPONENT
// =============================================================================

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    new ResizeObserver(() => this.initPages()).observe(this.slider);
    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));

    if (this.closest("media-gallery")) this.#createDotsNavigation();
  }

  #createDotsNavigation() {
    const container = document.createElement("div");
    container.className = "slider-dots mobile-only";
    this.sliderItems.forEach((_, i) => {
      const dot = document.createElement("button");
      dot.className = "slider-dot";
      dot.setAttribute("aria-label", `Go to slide ${i + 1}`);
      dot.addEventListener("click", () => this.goToSlide(i));
      container.appendChild(dot);
    });
    this.slider.parentNode.insertBefore(container, this.slider.nextSibling);
    this.dots = container.querySelectorAll(".slider-dot");
  }

  goToSlide(index) {
    this.setSlidePosition(this.sliderItems[index].offsetLeft);
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((el) => el.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement) this.currentPageElement.textContent = this.currentPage;
    if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages;

    this.dots?.forEach((dot, i) => dot.classList.toggle("active", i === this.currentPage - 1));

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(new CustomEvent("slideChanged", {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentPage - 1],
        },
      }));
    }

    if (this.enableSliderLooping) return;

    this.prevButton.toggleAttribute(
      "disabled",
      this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0
    );
    this.nextButton.toggleAttribute(
      "disabled",
      this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])
    );
  }

  isSlideVisible(element, offset = 0) {
    const lastVisible = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisible && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({ left: position, behavior: "smooth" });
  }
}
customElements.define("slider-component", SliderComponent);

// =============================================================================
// SLIDESHOW COMPONENT
// =============================================================================

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });
      [this.prevButton, this.nextButton].forEach((btn) => {
        btn.addEventListener("click", () => { this.announcementBarArrowButtonWasClicked = true; }, { once: true });
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    if (this.sliderAutoplayButton) {
      this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion?.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirst = this.currentPage === 1;
    const isLast = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirst && !isLast) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirst && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLast && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);
    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({ left: position });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");
    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    const active = this.sliderControlButtons[this.currentPage - 1];
    active?.classList.add("slider-counter__link--active");
    active?.setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const onButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || onButton) return;
      this.play();
    } else if (!this.reducedMotion?.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const onButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (onButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider?.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const position =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
    this.setSlidePosition(position);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const links = item.querySelectorAll("a");
      const isCurrent = index === this.currentPage - 1;
      links.forEach((a) => {
        isCurrent ? a.removeAttribute("tabindex") : a.setAttribute("tabindex", "-1");
      });
      item.setAttribute("aria-hidden", String(!isCurrent));
      isCurrent ? item.removeAttribute("tabindex") : item.setAttribute("tabindex", "-1");
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const count = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;
    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment + count) % count;

    const current = this.sliderItems[currentIndex];
    const next = this.sliderItems[nextIndex];
    const classIn = "announcement-bar-slider--fade-in";
    const classOut = "announcement-bar-slider--fade-out";

    const isFirst = currentIndex === 0;
    const isLast = currentIndex === count - 1;
    const goNext = (button === "next" && !isLast) || (button === "previous" && isFirst);
    const dir = goNext ? "next" : "previous";

    current.classList.add(`${classOut}-${dir}`);
    next.classList.add(`${classIn}-${dir}`);

    setTimeout(() => {
      current.classList.remove(`${classOut}-${dir}`);
      next.classList.remove(`${classIn}-${dir}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const offset =
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({ left: this.slider.scrollLeft + offset });
  }
}
customElements.define("slideshow-component", SlideshowComponent);

// =============================================================================
// COMPLEMENTARY VARIANT SELECTS
// =============================================================================

class ComplementaryVariantSelects extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const variantSelect = event.target.closest(".complementary-variant-select");
      if (!variantSelect) return;

      const input = variantSelect.querySelector("input");
      const card = event.target.closest(".card-wrapper");
      const cardContent = event.target.closest(".card__content");

      const variantIdInput = cardContent.querySelector(".product-variant-id");
      if (variantIdInput) variantIdInput.value = input.value;

      const priceWrapper = cardContent.querySelector(".quick-add__price-wrapper");
      const regularPrice = cardContent.querySelector(".quick-add__price");
      let comparePrice = cardContent.querySelector(".quick-add__compare-price");

      if (priceWrapper && regularPrice) {
        regularPrice.textContent = input.dataset.price;
        const hasDiscount =
          input.dataset.comparePrice && input.dataset.comparePrice !== input.dataset.price;

        if (hasDiscount) {
          if (comparePrice) {
            comparePrice.textContent = input.dataset.comparePrice;
            comparePrice.style.display = "";
          } else {
            comparePrice = document.createElement("s");
            comparePrice.className = "quick-add__compare-price";
            comparePrice.textContent = input.dataset.comparePrice;
            priceWrapper.insertBefore(comparePrice, regularPrice);
          }
        } else if (comparePrice) {
          comparePrice.style.display = "none";
          comparePrice.textContent = "";
        }

        card.dispatchEvent(
          new CustomEvent("variant:change", {
            bubbles: true,
            detail: {
              price: input.dataset.price,
              comparePrice: input.dataset.comparePrice,
            },
          })
        );
      } else {
        const oldPrice = cardContent.querySelector(".product-form__price");
        if (oldPrice) oldPrice.innerHTML = input.dataset.price;
      }

      cardContent.querySelector(".product-form__submit .price-item")?.let?.((el) => {
        el.innerHTML = input.dataset.price;
      });

      if (variantIdInput && window.BOLD?.BsubWidget?._renderPrices) {
        setTimeout(() => {
          const widget = cardContent.querySelector("[data-bsub-widget]");
          if (widget) window.BOLD.BsubWidget._renderPrices(widget);
        }, 50);
      }

      const img = card.querySelector(".media img");
      if (img && input.dataset.image) img.srcset = input.dataset.image;

      card.querySelectorAll("a").forEach((a) => { a.href = input.dataset.url; });

      card.querySelectorAll("input[type='radio']").forEach((i) => i.classList.remove("selected"));
      input.classList.add("selected");
    });
  }
}

customElements.define("complementary-variant-selects", ComplementaryVariantSelects);

// =============================================================================
// VARIANT SELECTS
// =============================================================================

class VariantSelects extends HTMLElement {
  static #cssInjected = false;

  static #injectHideStyle() {
    if (VariantSelects.#cssInjected) return;
    VariantSelects.#cssInjected = true;
    const style = document.createElement("style");
    style.id = "bsub-custom-hide-style";
    style.textContent =
      "body.hide-bsub .bold-subscriptions-widget, body.hide-bsub .bsub-widget, body.hide-bsub [data-bsub-widget] { display: none !important; }";
    document.head.appendChild(style);
  }

  connectedCallback() {
    VariantSelects.#injectHideStyle();

    this.addEventListener("change", (event) => {
      const target = this.#getInputForEventTarget(event.target);
      this.#updateSelectionMetadata(event);

      // Beide möglichen Attributnamen abfangen (data-compare-price UND data-compare-at-price)
      const comparePrice =
        target.dataset.comparePrice ??
        target.dataset.compareAtPrice ??
        null;

      if (target.dataset.price) {
        this.#updateStickyATCPrice(target.dataset.price, comparePrice);
      }

      // NUR aufrufen wenn das Attribut wirklich im Dataset vorhanden ist
      if (comparePrice !== null) {
        this.#updateCompareAtPrice(comparePrice);
      }

      if (target.dataset.unitPrice) {
        this.#updateUnitPrice(target.dataset.unitPrice);
      }

      const selectedValue = (target.value || "").toLowerCase().replace(/\s/g, "");
      if (selectedValue.includes("15ml")) {
        document.body.classList.add("hide-bsub");
      } else {
        document.body.classList.remove("hide-bsub");
        const widget = document.querySelector(
          ".bold-subscriptions-widget, .bsub-widget, [data-bsub-widget]"
        );
        if (widget && window.BOLD?.BsubWidget?._renderPrices) {
          setTimeout(() => window.BOLD.BsubWidget._renderPrices(widget), 150);
        }
      }

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: { event, target, selectedOptionValues: this.selectedOptionValues },
      });
    });
  }

  #updateStickyATCPrice(price, comparePrice = null) {
    document.querySelectorAll(
      ".sticky-atc-button-price .price-item--sale, .product-form__submit .price-item--sale, .product-form__submit .product-form__price"
    ).forEach((el) => { el.innerHTML = price; });

    if (comparePrice !== null) {
      document.querySelectorAll(
        ".sticky-atc-button-price .price-item--regular, .product-form__submit .price-item--regular"
      ).forEach((el) => {
        el.innerHTML = comparePrice;
        el.style.display = comparePrice ? "" : "none";
      });
    }
  }

  #updateCompareAtPrice(comparePrice) {
    const container = this.closest(".product__info-container, product-info");
    if (!container) return;

    const priceWrapper = container.querySelector(".price");
    const compareEl =
      container.querySelector(".price__compare .price-item--regular") ??
      container.querySelector(".price-item--regular");
    if (!priceWrapper || !compareEl) return;

    const hasDiscount = Boolean(comparePrice?.trim());
    priceWrapper.classList.toggle("price--on-sale", hasDiscount);
    compareEl.innerHTML = hasDiscount ? comparePrice : "";
    compareEl.style.display = hasDiscount ? "" : "none";
  }

  #updateUnitPrice(unitPrice) {
    const el = this.closest(".product__info-container")?.querySelector("#unit-price");
    if (el) el.innerHTML = unitPrice;
  }

  #updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      target.querySelector("option[selected]")?.removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const swatchEl = target.closest(".product-form__input")?.querySelector("[data-selected-value] > .swatch");
      if (!swatchEl) return;

      swatchEl.style.setProperty("--swatch--background", swatchValue || "unset");
      swatchEl.classList.toggle("swatch--unavailable", !swatchValue);
      swatchEl.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selected = target.closest(".product-form__input")?.querySelector("[data-selected-value]");
      if (selected) selected.innerHTML = value;
    }
  }

  #getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

// =============================================================================
// PRODUCT RECOMMENDATIONS
// =============================================================================

class ProductRecommendations extends HTMLElement {
  #observer;

  connectedCallback() {
    this.#initializeRecommendations(this.dataset.productId);
  }

  #initializeRecommendations(productId) {
    this.#observer?.unobserve(this);
    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.#loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.#observer.observe(this);
  }

  #loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((r) => r.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recs = html.querySelector("product-recommendations");
        if (recs?.innerHTML.trim()) this.innerHTML = recs.innerHTML;
        if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
          this.remove();
        }
        if (html.querySelector(".grid__item")) this.classList.add("product-recommendations--loaded");
      })
      .catch(console.error);
  }
}
customElements.define("product-recommendations", ProductRecommendations);

// =============================================================================
// ACCOUNT ICON
// =============================================================================

class AccountIcon extends HTMLElement {
  connectedCallback() {
    this.icon = this.querySelector(".icon");
    document.addEventListener("storefront:signincompleted", this.#handleSignIn.bind(this));
  }

  #handleSignIn(event) {
    if (event?.detail?.avatar) this.icon?.replaceWith(event.detail.avatar.cloneNode());
  }
}
customElements.define("account-icon", AccountIcon);

// =============================================================================
// BULK ADD
// =============================================================================

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.#setRequestStarted(false);
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) this.#sendRequest(this.queue);
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  #sendRequest(queue) {
    this.#setRequestStarted(true);
    const items = Object.fromEntries(queue.map(({ id, quantity }) => [parseInt(id), quantity]));
    this.queue = this.queue.filter((el) => !queue.includes(el));
    this.updateMultipleQty(items);
  }

  #setRequestStarted(value) {
    this._requestStarted = value;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const { min, max, step } = event.target;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min));
    } else if (inputValue > parseInt(max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace("[max]", max));
    } else if (inputValue % parseInt(step) !== 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace("[step]", step));
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) customElements.define("bulk-add", BulkAdd);

// =============================================================================
// CART PERFORMANCE
// =============================================================================

class CartPerformance {
  static #PREFIX = "cart-performance";

  static createStartingMarker(name) {
    return performance.mark(`${CartPerformance.#PREFIX}:${name}:start`);
  }

  static measureFromEvent(name, event) {
    const prefix = `${CartPerformance.#PREFIX}:${name}`;
    performance.mark(`${prefix}:start`, { startTime: event.timeStamp });
    performance.mark(`${prefix}:end`);
    performance.measure(name, `${prefix}:start`, `${prefix}:end`);
  }

  static measureFromMarker(name, startMarker) {
    const end = `${CartPerformance.#PREFIX}:${name}:end`;
    performance.mark(end);
    performance.measure(name, startMarker.name, end);
  }

  static measure(name, callback) {
    const prefix = `${CartPerformance.#PREFIX}:${name}`;
    performance.mark(`${prefix}:start`);
    callback();
    performance.mark(`${prefix}:end`);
    performance.measure(name, `${prefix}:start`, `${prefix}:end`);
  }
}

// =============================================================================
// STICKY ATC
// =============================================================================

class StickyATC extends HTMLElement {
  connectedCallback() {
    this.productSubmitButton = document.querySelector(
      "product-info .product__info-container .product-form__submit"
    );
    this.stickyButton = this.querySelector(".sticky-atc-button");

    if (!this.productSubmitButton) {
      console.warn("StickyATC: Product submit button not found");
      return;
    }

    this.stickyButton.addEventListener("click", this.#handleATCSubmit.bind(this));
    this.#setupObserver();
  }

  disconnectedCallback() {
    this.#destroyObserver();
  }

  #handleATCSubmit() {
    this.productSubmitButton.click();
  }

  #setupObserver() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.classList.remove("active");
        } else {
          const rect = this.productSubmitButton.getBoundingClientRect();
          this.classList.toggle("active", rect.bottom < 0);
        }
      },
      { root: null, rootMargin: "50px", threshold: 0 }
    );
    this.observer.observe(this.productSubmitButton);
  }

  #destroyObserver() {
    this.observer?.disconnect();
    this.observer = null;
  }
}

if (!customElements.get("sticky-atc")) customElements.define("sticky-atc", StickyATC);

// =============================================================================
// BOLD SUBSCRIPTIONS — DYNAMIC TOGGLE, AUTO-SUBSCRIBER & PRICE SYNC
// =============================================================================

document.addEventListener("DOMContentLoaded", () => {
  const BSUB_SELECTOR = ".bold-subscriptions-widget, .bsub-widget, [data-bsub-widget]";

  let isSyncingPrice = false;
  let bsubInterval = null;
  let bsubRadioListener = null;
  let userHasChosenPurchaseType = false;

  function getBsubWidget() {
    return document.querySelector(BSUB_SELECTOR);
  }

  function parseGermanPrice(str) {
    if (!str) return NaN;
    return parseFloat(String(str).replace(/\./g, "").replace(",", ".").replace(/[^\d.]/g, ""));
  }

  function formatGermanPrice(val) {
    return val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  }

  function applyPriceToButtons(price) {
    if (!price) return;
    isSyncingPrice = true;
    [
      ".product-form__submit .product-form__price",
      ".product-form__submit .price-item",
      ".sticky-atc-button-price .price-item",
    ].forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (el.innerHTML !== price) el.innerHTML = price;
      });
    });
    setTimeout(() => { isSyncingPrice = false; }, 60);
  }

  function syncBoldPriceToButton() {
    if (isSyncingPrice) return;

    const widgetHidden = document.body.classList.contains("hide-bsub-widget");
    const widget = getBsubWidget();

    let finalPrice = null;
    let isSubscription = false;
    let discountMultiplier = 1;

    if (widget && !widgetHidden) {
      const checkedRadio = widget.querySelector("input[type='radio']:checked");
      if (checkedRadio) {
        const val = (checkedRadio.value || "").toLowerCase();
        isSubscription = val !== "" && val !== "0" && val !== "one_time" && val !== "false";

        if (checkedRadio.dataset.price) {
          finalPrice = checkedRadio.dataset.price;
        } else {
          const planEl = checkedRadio.closest(
            "label, .bsub-widget__group, .bsub-widget__plan, .bsub-widget__item"
          );
          if (planEl) {
            const prices = planEl.innerText.match(/\d[\d.,]*\s*€/g);
            if (prices?.length) {
              finalPrice = prices[prices.length - 1].trim();
              if (prices.length > 1) {
                const reg = parseGermanPrice(prices[0]);
                const sub = parseGermanPrice(finalPrice);
                if (reg > 0 && sub > 0) discountMultiplier = sub / reg;
              }
            }
          }
        }
      }
    }

    if (!finalPrice) {
      const activeVariant = document.querySelector(
        "variant-selects input[type='radio']:checked, variant-selects select option[selected]"
      );
      if (activeVariant?.dataset.price) finalPrice = activeVariant.dataset.price;
    }

    applyPriceToButtons(finalPrice);

    const unitPriceEl = document.getElementById("unit-price");
    if (unitPriceEl) {
      if (!unitPriceEl.dataset.basePrice) {
        unitPriceEl.dataset.basePrice = unitPriceEl.innerText.trim();
      }
      const base = unitPriceEl.dataset.basePrice;
      const target = isSubscription
        ? formatGermanPrice(parseGermanPrice(base) * discountMultiplier)
        : base;
      if (unitPriceEl.innerText !== target) unitPriceEl.innerText = target;
    }
  }

  function attachBsubRadioListeners() {
    const widget = getBsubWidget();
    if (!widget) return false;

    const radios = widget.querySelectorAll("input[type='radio']");
    if (!radios.length) return false;

    radios.forEach((radio) => {
      radio.removeEventListener("change", onBsubRadioChange);
      radio.addEventListener("change", onBsubRadioChange);
    });
    return true;
  }

  function onBsubRadioChange() {
    setTimeout(syncBoldPriceToButton, 80);
  }

  function watchForBsubWidget() {
    if (attachBsubRadioListeners()) {
      syncBoldPriceToButton();
      return;
    }

    const observer = new MutationObserver(() => {
      if (attachBsubRadioListeners()) {
        observer.disconnect();
        syncBoldPriceToButton();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000);
  }

  function forceBoldSubscription() {
    if (document.body.classList.contains("hide-bsub-widget")) {
      syncBoldPriceToButton();
      return;
    }

    stopBsubInterval();

    let attempts = 0;
    let didClick = false;

    bsubInterval = setInterval(() => {
      attempts++;
      const widget = getBsubWidget();

      if (widget) {
        const radios = widget.querySelectorAll("input[type='radio']");
        if (radios.length) {
          let subRadio = null;
          for (const radio of radios) {
            const container = radio.closest(
              "label, .bsub-widget__group, .bsub-widget__group-header, .bsub-widget__item"
            );
            if (container?.innerText.toLowerCase().includes("abonniere")) {
              subRadio = radio;
              break;
            }
          }
          subRadio ??= radios.length > 1 ? radios[1] : null;

          if (subRadio) {
            if (!subRadio.checked) {
              subRadio.click();
              subRadio.checked = true;
              subRadio.dispatchEvent(new Event("change", { bubbles: true }));
              didClick = true;
            }

            if (subRadio.checked) {
              stopBsubInterval();
              attachBsubRadioListeners();
              setTimeout(syncBoldPriceToButton, 120);
              return;
            }
          }
        }
      }

      if (attempts > 40) {
        stopBsubInterval();
        syncBoldPriceToButton();
      }
    }, 100);
  }

  function stopBsubInterval() {
    if (bsubInterval) {
      clearInterval(bsubInterval);
      bsubInterval = null;
    }
  }

  document.addEventListener("change", (event) => {
    const target = event.target;

    if (target?.classList.contains("product-variant-id") || target?.name === "id") {
      const variantId = target.value;

      const unitPriceEl = document.getElementById("unit-price");
      if (unitPriceEl) delete unitPriceEl.dataset.basePrice;

      if (window.bsubVariantMap?.[variantId] === false) {
        document.body.classList.add("hide-bsub-widget");
        setTimeout(syncBoldPriceToButton, 50);
      } else if (window.bsubVariantMap?.[variantId] === true) {
        document.body.classList.remove("hide-bsub-widget");
        const widget = getBsubWidget();
        if (widget && window.BOLD?.BsubWidget?._renderPrices) {
          setTimeout(() => window.BOLD.BsubWidget._renderPrices(widget), 50);
        }
        if (!userHasChosenPurchaseType) {
          forceBoldSubscription();
        } else {
          setTimeout(syncBoldPriceToButton, 100);
        }
      } else {
        setTimeout(syncBoldPriceToButton, 100);
      }
    }
  });

  document.addEventListener("mousedown", (event) => {
    if (event.target.closest(BSUB_SELECTOR)) {
      stopBsubInterval();
      userHasChosenPurchaseType = true;
    }
  });

  watchForBsubWidget();
  forceBoldSubscription();
});
